// useRightDragZoom.ts
import { useEffect, useRef } from 'react'

type UseRightDragZoomOpts = {
  map: H.Map | null // your initialized HERE map
  container: HTMLElement | null // the same DOM element you passed to H.Map
  minBoxPixels?: number // ignore tiny boxes (default 12)
  onZoom?: (rect: H.geo.Rect) => void // optional callback after zoom
}

export function useRightDragZoom({
  map,
  container,
  minBoxPixels = 12,
  onZoom,
}: UseRightDragZoomOpts) {
  const startRef = useRef<{ x: number; y: number } | null>(null)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const drawingRef = useRef(false)

  useEffect(() => {
    if (!map || !container) return

    // Create the overlay box once
    const box = document.createElement('div')
    box.style.position = 'absolute'
    box.style.pointerEvents = 'none'
    box.style.border = '2px dashed #2a6'
    box.style.background = 'rgba(50, 200, 120, 0.15)'
    box.style.display = 'none'
    box.style.left = '0'
    box.style.top = '0'
    container.appendChild(box)
    boxRef.current = box

    // Prevent the browser context menu on right-drag
    const onContextMenu = (e: MouseEvent) => {
      if (drawingRef.current) e.preventDefault()
    }
    container.addEventListener('contextmenu', onContextMenu)

    const getRelative = (e: MouseEvent) => {
      const r = container.getBoundingClientRect()
      return { x: e.clientX - r.left, y: e.clientY - r.top }
    }

    const onMouseDown = (e: MouseEvent) => {
      // Right button only
      if (e.button !== 2) return
      // Start drawing
      const p = getRelative(e)
      startRef.current = p
      drawingRef.current = true

      // show box at 0 size
      if (boxRef.current) {
        boxRef.current.style.display = 'block'
        boxRef.current.style.left = `${p.x}px`
        boxRef.current.style.top = `${p.y}px`
        boxRef.current.style.width = '0px'
        boxRef.current.style.height = '0px'
      }

      // while drawing, stop map's default interactions from seeing these events
      e.preventDefault()
      e.stopPropagation()
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!drawingRef.current || !startRef.current) return
      if (boxRef.current?.style.display === 'none') {
        console.log(
          '[MapView][SH-568] useRightDragZoom stuck — drawingRef=true but box hidden',
          {
            ts: Date.now(),
          },
        )
      }
      const p = getRelative(e)
      const sx = startRef.current.x
      const sy = startRef.current.y

      const left = Math.min(sx, p.x)
      const top = Math.min(sy, p.y)
      const w = Math.abs(p.x - sx)
      const h = Math.abs(p.y - sy)

      if (boxRef.current) {
        boxRef.current.style.left = `${left}px`
        boxRef.current.style.top = `${top}px`
        boxRef.current.style.width = `${w}px`
        boxRef.current.style.height = `${h}px`
      }

      e.preventDefault()
      e.stopPropagation()
    }

    const finish = (e: MouseEvent | null) => {
      if (!drawingRef.current) return
      drawingRef.current = false

      try {
        if (!startRef.current || !boxRef.current) return

        const sx = startRef.current.x
        const sy = startRef.current.y

        const end = e ? getRelative(e as MouseEvent) : { x: sx, y: sy }
        const left = Math.min(sx, end.x)
        const top = Math.min(sy, end.y)
        const w = Math.abs(end.x - sx)
        const h = Math.abs(end.y - sy)

        // Hide the box
        boxRef.current.style.display = 'none'

        // Ignore tiny boxes
        if (w < minBoxPixels || h < minBoxPixels) return

        // Convert screen pixels to geo
        const nw = map.screenToGeo(left, top) // top-left
        const se = map.screenToGeo(left + w, top + h) // bottom-right

        // Build a Rect (north, west, south, east)
        const north = Math.max(nw.lat, se.lat)
        const south = Math.min(nw.lat, se.lat)
        const west = Math.min(nw.lng, se.lng)
        const east = Math.max(nw.lng, se.lng)
        const rect = new H.geo.Rect(north, west, south, east)

        // Zoom to it without animation
        map.getViewModel().setLookAtData({ bounds: rect }, false)

        onZoom?.(rect)
      } finally {
        startRef.current = null
      }
    }

    const onMouseUp = (e: MouseEvent) => {
      if (e.button === 2) {
        e.preventDefault()
        e.stopPropagation()
        finish(e)
      }
    }

    const onMouseLeave = () => {
      // Cancel if user leaves container mid-drag
      if (drawingRef.current) finish(null)
    }

    container.addEventListener('mousedown', onMouseDown, { passive: false })
    container.addEventListener('mousemove', onMouseMove, { passive: false })
    container.addEventListener('mouseup', onMouseUp, { passive: false })
    container.addEventListener('mouseleave', onMouseLeave)

    return () => {
      container.removeEventListener('contextmenu', onContextMenu)
      container.removeEventListener('mousedown', onMouseDown as any)
      container.removeEventListener('mousemove', onMouseMove as any)
      container.removeEventListener('mouseup', onMouseUp as any)
      container.removeEventListener('mouseleave', onMouseLeave as any)
      if (boxRef.current && boxRef.current.parentNode) {
        boxRef.current.parentNode.removeChild(boxRef.current)
      }
      boxRef.current = null
    }
  }, [map, container, minBoxPixels, onZoom])
}
